<template>
  <Content
    class="manifest-list"
    aside-stretched
    full-width
    remove-card-wrapper
    remove-aside-card-padding
  >
    <template #header>
      <div
        class="content-header flex justify-content-between align-items-center"
      >
        <div
          class="content-heading-container flex flex-gap-1 align-items-center"
        >
          <h2 class="m-0">{{ t('manifests.manifests', 2) }}</h2>
          <LoadingIndicator :loading="results.loading" />
        </div>

        <span class="flex flex-gap-1 align-items-center">
          <RygenButton
            v-if="parcelManifestingEnabled && $hasAuthority('shipment', 'read')"
            mode="secondary"
            :label="t('manifests.createFromShipments')"
            icon="pi pi-plus"
            data-testid="manifest-list-create-button"
            @click="manifestModal.visible = true"
          />
        </span>
      </div>
    </template>

    <!--
      Deferred (CS-11545): a "future scheduled manifests" section and links to
      configure scheduling. The scheduling config (per-account manifest time,
      carrier end-of-day flag, scan job) does not exist yet, so this is an inert
      placeholder to be wired up when that config ships.
    -->
    <Message severity="info" :closable="false" class="manifest-scheduled-note">
      {{ t('manifests.scheduledComingSoon') }}
    </Message>

    <SearchResultTableAndFilterWrapper>
      <template #filters>
        <ManifestSearchBuilder
          id="manifest-search-builder"
          :model-value="criteria"
          @update:model-value="onCriteriaChange"
        />
      </template>

      <template #default>
        <ManifestSearchResultView
          v-model:columns="columns"
          :manifest-search-criteria="criteria"
          :results="results"
          :column-options="MANIFEST_COLUMNS"
          @page="onPage"
          @sort="onSort"
          @edited-columns="onEditedColumns"
          @refresh-no-cache="onRefreshNoCache"
        />
      </template>
    </SearchResultTableAndFilterWrapper>

    <ManifestModal
      v-model:visible="manifestModal.visible"
      @success="onManifestSuccess"
    />

    <ManifestSuccessModal
      v-model:visible="manifestSuccessModal.visible"
      :result="manifestSuccessModal.results"
    />
  </Content>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { CancelTokenSource, default as axios } from 'axios';
import type { Ref } from 'vue';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  usePartitionedStorageKey,
  useQueryParamsForSearch,
  useToast,
} from '@rygen/rygen-ui/composables';
import { ColumnSpecification, Loadable } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  MANIFEST_COLUMNS,
  ManifestResponse,
  ManifestSearchCriteria,
  Manifest,
  ManifestSearchResult,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import ManifestSearchBuilder from '@/components/manifest/ManifestSearchBuilder.vue';
import ManifestSearchResultView from '@/components/manifest/ManifestSearchResult.vue';
import ManifestModal from '@/components/shipment/manifest/ManifestModal.vue';
import ManifestSuccessModal from '@/components/shipment/manifest/ManifestSuccessModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import SearchResultTableAndFilterWrapper from '@rygen/rygen-ui/components/ui/SearchResultTableAndFilterWrapper.vue';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';
import Message from 'primevue/message';

const { currentUser, selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const toast = useToast();
const { storageKey: columnsLSKey } = usePartitionedStorageKey(
  'manifestColumns',
  { currentUser, selectedDomain },
);

const results: Ref<Loadable<ManifestSearchResult>> = ref({
  loading: false,
  data: new ManifestSearchResult(),
});

const parcelManifestingEnabled = useFlag('parcel-manifest-feat-flag');
const manifestModal = ref({ visible: false });
const manifestSuccessModal = ref({
  visible: false,
  results: new ManifestResponse(),
});

const onManifestSuccess = (event: ManifestResponse) => {
  manifestSuccessModal.value.results = new ManifestResponse({ ...event });
  manifestSuccessModal.value.visible = true;
  runSearchDebounced();
};

const runSearch = async (
  searchCriteria: ManifestSearchCriteria,
  cancelRequest?: CancelTokenSource,
) => {
  try {
    results.value.loading = true;
    results.value.data = await api.shipmentManifest.searchManifests(
      searchCriteria,
      cancelRequest,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }
    results.value.error = e;
    console.error('Error loading manifests', e);
    toast.addError({
      detail: t('errors.loadingParam', { param: t('manifests.manifests', 2) }),
      error: e,
    });
  } finally {
    results.value.loading = false;
  }
};

const { criteria, runSearchDebounced } = useQueryParamsForSearch({
  factoryMethod: (opts?: Partial<ManifestSearchCriteria>) =>
    new ManifestSearchCriteria(opts),
  routeName: 'manifestList',
  runSearch,
});

const columns: Ref<ColumnSpecification[]> = ref(MANIFEST_COLUMNS);

const columnsByKey = new Map(MANIFEST_COLUMNS.map((col) => [col.key, col]));
const getColumnFromKey = (key: string) => columnsByKey.get(key);

const loadColumns = (lsKey: string) => {
  const stored = ColumnSpecification.deserialize(
    localStorage.getItem(lsKey),
    getColumnFromKey,
  );
  columns.value = stored ?? MANIFEST_COLUMNS;
};

const onCriteriaChange = (newVal: ManifestSearchCriteria) => {
  criteria.value = new ManifestSearchCriteria(newVal);
  criteria.value.page = 0;
};

const onPage = (event: DataTablePageEvent) => {
  criteria.value.pageSize = event.rows as number;
  criteria.value.page = event.page as number;
};

const onSort = (event: DataTableSortEvent) => {
  criteria.value.sortField = event.sortField as keyof Manifest | undefined;
  criteria.value.sortOrder = (event.sortOrder ?? 0) as -1 | 0 | 1;
};

const onEditedColumns = (newColumns: ColumnSpecification[]) => {
  localStorage.setItem(
    columnsLSKey.value,
    ColumnSpecification.serialize(newColumns.filter(notNull)),
  );
};

const onRefreshNoCache = () => {
  runSearchDebounced();
};

watch(
  columnsLSKey,
  (newVal: string) => {
    loadColumns(newVal);
  },
  { immediate: true },
);
</script>

<style>
.manifest-list {
  .manifest-scheduled-note {
    margin: var(--sp-2);
  }
}
</style>
